import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Shirt, Palette, TrendingUp, Box, DollarSign, Zap } from 'lucide-react';

const AIImageGeneratorPOD = () => {
  const faqs = [
    {
      question: "Can I use AI-generated images for print-on-demand products?",
      answer: "Yes! All images generated with our platform come with full commercial rights, perfect for Printful, Printify, Merch by Amazon, Redbubble, and other POD platforms."
    },
    {
      question: "What resolution do I need for print-on-demand?",
      answer: "Most POD platforms require 300 DPI for quality prints. Our AI generates high-resolution images suitable for t-shirts, mugs, posters, and more."
    },
    {
      question: "Can I create transparent PNG files for POD?",
      answer: "Yes! We offer background removal tools to create transparent PNGs perfect for t-shirt designs and other print products."
    },
    {
      question: "How do I create consistent design collections?",
      answer: "Use our batch generation feature with style prompts to create cohesive collections. Generate 50+ matching designs in minutes for your store."
    }
  ];

  const features = [
    {
      icon: <Shirt className="w-6 h-6" />,
      title: "POD-Ready Designs",
      description: "Generate designs optimized for t-shirts, hoodies, mugs, phone cases, and more"
    },
    {
      icon: <Box className="w-6 h-6" />,
      title: "High-Resolution Output",
      description: "300 DPI images ready for print - no upscaling needed"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Trending Niches",
      description: "AI trained on bestselling POD niches: motivation, pets, hobbies, humor"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Design Generation",
      description: "Create entire product collections with 50-100+ designs in one session"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Mockup Ready",
      description: "Generate designs that look great on product mockups instantly"
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Commercial License",
      description: "Full commercial rights included - sell unlimited products"
    }
  ];

  const platforms = [
    "Printful",
    "Printify",
    "Merch by Amazon",
    "Redbubble",
    "TeeSpring",
    "Zazzle",
    "Society6",
    "Spreadshirt"
  ];

  const productTypes = [
    "T-shirt designs with transparent backgrounds",
    "All-over print patterns and seamless textures",
    "Mug and drinkware graphics",
    "Phone case artwork and illustrations",
    "Poster and wall art designs",
    "Sticker sheet collections",
    "Pillow and home decor patterns",
    "Tote bag and accessory designs"
  ];

  return (
    <>
      <SEO
        title="AI Print-on-Demand Image Generator | POD Design Tool for Merch"
        description="Create print-on-demand designs with AI. Generate t-shirt graphics, mug designs, posters for Printful, Printify, Merch by Amazon. Bulk create POD-ready images."
        keywords="print on demand ai generator, pod design tool, ai t-shirt design, merch by amazon generator, printful design ai, ai mockup generator, pod image creator, bulk pod designs"
        url="/ai-image-generator-print-on-demand"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Print-on-Demand Generator', url: '/ai-image-generator-print-on-demand' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-purple-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-orange-500/10 text-orange-500 border-orange-500/20">
                Print-on-Demand AI Designs
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Print-on-Demand Generator
                <span className="block text-orange-500">Create Bestselling Merch Designs</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate POD-ready designs with AI. T-shirts, mugs, posters, and more. 
                High-resolution, commercial licensed, bulk creation for your store.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                  <Link to="/try-generator">
                    Create POD Designs Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Design Examples</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Logos */}
        <section className="py-12 border-y border-border">
          <div className="container mx-auto px-4">
            <p className="text-center text-muted-foreground mb-6">Works with all major POD platforms</p>
            <div className="flex flex-wrap justify-center gap-6">
              {platforms.map((platform, index) => (
                <Badge key={index} variant="secondary" className="text-sm py-2 px-4">
                  {platform}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Everything You Need for POD Success
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-orange-500/10 hover:border-orange-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Product Types */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Create Designs for Any Product Type
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {productTypes.map((product, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span>{product}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Your POD Empire with AI Designs
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 5,000+ POD sellers using AI to create bestselling designs. 
                No design skills needed.
              </p>
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                <Link to="/try-generator">
                  Generate POD Designs Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorPOD;
